import * as THREE from 'three'

import Experience from '../Experience'

interface BloodParticle {
  mesh: THREE.Mesh
  material: THREE.MeshBasicMaterial
  velocity: THREE.Vector3
  life: number
  maxLife: number
  gravity: number
  drag: number
  spin: number
  canSplat: boolean
}

interface BloodPool {
  mesh: THREE.Mesh
  material: THREE.MeshBasicMaterial
  age: number
  growTime: number
  maxSize: number
  jitter: number
  active: boolean
}

const MIST_LIFE = 0.28
const DROP_LIFE = 1.1
const UP = new THREE.Vector3(0, 1, 0)

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v)
const clampByte = (v: number): number => (v < 0 ? 0 : v > 255 ? 255 : v)

/** Smooth multi-octave noise from summed sines — organic but continuous, so a
 * decal scaled up on the floor never shows the per-pixel hash "dither" that read
 * as pixelation. Inputs are normalized coords; output ~[0,1]. */
const fbm = (x: number, y: number, seed: number): number => {
  let v = 0
  let amp = 0.5
  let freq = 1
  for (let o = 0; o < 4; o += 1) {
    v +=
      amp *
      Math.sin(x * freq * 1.7 + seed * (o + 1)) *
      Math.cos(y * freq * 1.9 - seed * (o + 1.3))
    freq *= 2
    amp *= 0.5
  }
  return v * 0.5 + 0.5
}

/** Trilinear + anisotropic filtering so floor decals stay sharp at grazing
 * angles and don't shimmer/pixelate (the main "HD" win for decals). */
const configureDecalTexture = (tex: THREE.Texture, anisotropy: number): void => {
  tex.colorSpace = THREE.SRGBColorSpace
  tex.magFilter = THREE.LinearFilter
  tex.minFilter = THREE.LinearMipmapLinearFilter
  tex.generateMipmaps = true
  tex.anisotropy = anisotropy
  tex.needsUpdate = true
}

const newCanvas = (size: number): [HTMLCanvasElement, CanvasRenderingContext2D, ImageData] => {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('2D canvas unavailable for blood texture')
  return [canvas, ctx, ctx.createImageData(size, size)]
}

/** Spray/spatter sprite: a soft mist blob (soft) or an irregular spatter disc.
 * Density variation uses smooth fbm, not a per-pixel hash. */
const makeBloodTexture = (
  size: number,
  color: [number, number, number],
  soft: boolean,
): THREE.CanvasTexture => {
  const [canvas, ctx, image] = newCanvas(size)
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const nx = (x / (size - 1)) * 2 - 1
      const ny = (y / (size - 1)) * 2 - 1
      const dist = Math.hypot(nx, ny)
      const angle = Math.atan2(ny, nx)
      const edgeR =
        0.84 +
        Math.sin(angle * 4 + 1.3) * 0.08 +
        Math.sin(angle * 9 - 0.7) * 0.05
      let alpha = soft
        ? Math.max(0, 1 - dist) * (1 - THREE.MathUtils.smoothstep(dist, 0.12, edgeR))
        : 1 - THREE.MathUtils.smoothstep(dist, edgeR * 0.74, edgeR)
      const mottle = 0.78 + 0.22 * fbm(nx * 2.6, ny * 2.6, 7.0)
      alpha = clamp01(alpha * mottle)
      const tone = 0.9 + 0.1 * fbm(nx * 2, ny * 2, 2.0)
      const i = (y * size + x) * 4
      image.data[i] = clampByte(color[0] * tone)
      image.data[i + 1] = color[1]
      image.data[i + 2] = color[2]
      image.data[i + 3] = Math.round(alpha * 255)
    }
  }
  ctx.putImageData(image, 0, 0)
  return new THREE.CanvasTexture(canvas)
}

/** A thick blood pool: near-black maroon where it pools, brighter red at the
 * thin rim, an organic boundary, smooth wet mottling, and a soft specular sheen
 * so it reads as fresh liquid (not a flat disc). High-res + smooth = no pixels. */
const makePoolTexture = (size: number, seed: number): THREE.CanvasTexture => {
  const [canvas, ctx, image] = newCanvas(size)
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const nx = (x / (size - 1)) * 2 - 1
      const ny = (y / (size - 1)) * 2 - 1
      const dist = Math.hypot(nx, ny)
      const angle = Math.atan2(ny, nx)
      const edge =
        0.66 +
        Math.sin(angle * 3 + seed) * 0.08 +
        Math.sin(angle * 5 - seed * 1.6) * 0.05 +
        Math.sin(angle * 8 + seed * 0.7) * 0.035
      const alpha = 1 - THREE.MathUtils.smoothstep(dist, edge - 0.14, edge)
      const i = (y * size + x) * 4
      if (alpha <= 0.002) {
        image.data[i + 3] = 0
        continue
      }
      // 1 at the thick centre, 0 at the thin rim.
      const thick = THREE.MathUtils.smoothstep(1 - dist / edge, 0, 1)
      const mottle = 0.5 + 0.5 * fbm(nx * 2.4, ny * 2.4, seed)
      let r = THREE.MathUtils.lerp(138, 46, thick) * (0.82 + 0.18 * mottle)
      let g = THREE.MathUtils.lerp(16, 2, thick)
      let b = THREE.MathUtils.lerp(12, 3, thick)
      // Soft sheen highlight, up-left, only where the blood is thick (wet).
      const sx = (nx + 0.28) * 1.5
      const sy = (ny + 0.34) * 1.7
      const sheen = Math.max(0, 1 - Math.hypot(sx, sy)) ** 2 * thick * 0.6
      r += sheen * 120
      g += sheen * 36
      b += sheen * 30
      image.data[i] = clampByte(r)
      image.data[i + 1] = clampByte(g)
      image.data[i + 2] = clampByte(b)
      image.data[i + 3] = Math.round(clamp01(alpha) * 245)
    }
  }
  ctx.putImageData(image, 0, 0)
  return new THREE.CanvasTexture(canvas)
}

export default class Blood {
  private readonly particles: BloodParticle[] = []
  private readonly splats: THREE.Mesh[] = []
  private readonly pools: BloodPool[] = []
  private readonly particleTextures: THREE.Texture[]
  private readonly splatTextures: THREE.Texture[]
  private readonly poolTextures: THREE.Texture[]
  private particleNext = 0
  private splatNext = 0
  private poolNext = 0
  private readonly tmp = new THREE.Vector3()
  private readonly tmp2 = new THREE.Vector3()
  private readonly forward = new THREE.Vector3()
  private readonly backDir = new THREE.Vector3()
  private readonly right = new THREE.Vector3()
  private readonly up = new THREE.Vector3()
  private readonly down = new THREE.Vector3(0, -1, 0)
  private readonly floorOrigin = new THREE.Vector3()
  private readonly normal = new THREE.Vector3()

  constructor(
    scene: THREE.Scene,
    // General map raycast (origin, dir, far) — used to anchor spatter onto real
    // surfaces (the wall behind the victim, the floor) instead of mid-air.
    private readonly castRay?: (
      origin: THREE.Vector3,
      dir: THREE.Vector3,
      far: number,
    ) => THREE.Intersection | null,
  ) {
    // Anisotropic + mipmapped filtering keeps the decals crisp on the floor at
    // grazing angles — the main fix for the "pixelated" look.
    const aniso = Experience.instance.renderer.instance.capabilities.getMaxAnisotropy()

    this.particleTextures = [
      makeBloodTexture(128, [150, 0, 0], true),
      makeBloodTexture(128, [95, 0, 0], true),
      makeBloodTexture(128, [55, 0, 0], false),
    ]
    this.splatTextures = [
      makeBloodTexture(256, [82, 0, 0], false),
      makeBloodTexture(256, [115, 5, 0], false),
      makeBloodTexture(256, [42, 0, 0], false),
    ]
    this.poolTextures = [
      makePoolTexture(512, 0.4),
      makePoolTexture(512, 2.1),
      makePoolTexture(512, 3.7),
    ]
    for (const tex of [...this.particleTextures, ...this.splatTextures, ...this.poolTextures]) {
      configureDecalTexture(tex, aniso)
    }

    const particleGeometry = new THREE.PlaneGeometry(1, 1)
    for (let i = 0; i < 112; i += 1) {
      const material = new THREE.MeshBasicMaterial({
        map: this.particleTextures[0],
        color: 0xffffff,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      })
      const mesh = new THREE.Mesh(particleGeometry, material)
      mesh.visible = false
      mesh.frustumCulled = false
      scene.add(mesh)
      this.particles.push({
        mesh,
        material,
        velocity: new THREE.Vector3(),
        life: 0,
        maxLife: 1,
        gravity: 0,
        drag: 0,
        spin: 0,
        canSplat: false,
      })
    }

    const splatGeometry = new THREE.PlaneGeometry(1, 1)
    for (let i = 0; i < 80; i += 1) {
      const material = new THREE.MeshBasicMaterial({
        map: this.splatTextures[0],
        color: 0xffffff,
        transparent: true,
        opacity: 0.86,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: -8,
        polygonOffsetUnits: -8,
        side: THREE.DoubleSide,
      })
      const mesh = new THREE.Mesh(splatGeometry, material)
      mesh.visible = false
      mesh.frustumCulled = false
      mesh.renderOrder = 3
      scene.add(mesh)
      this.splats.push(mesh)
    }

    const poolGeometry = new THREE.PlaneGeometry(1, 1)
    for (let i = 0; i < 24; i += 1) {
      const material = new THREE.MeshBasicMaterial({
        map: this.poolTextures[0],
        color: 0xffffff,
        transparent: true,
        opacity: 0.92,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: -6,
        polygonOffsetUnits: -6,
        side: THREE.DoubleSide,
      })
      const mesh = new THREE.Mesh(poolGeometry, material)
      mesh.visible = false
      mesh.frustumCulled = false
      mesh.renderOrder = 2 // under the spatter detail
      scene.add(mesh)
      this.pools.push({
        mesh,
        material,
        age: 0,
        growTime: 1,
        maxSize: 1,
        jitter: 1,
        active: false,
      })
    }
  }

  /** A bullet wound: a short spray, spatter on the wall behind the victim, and a
   * few drops on the floor. */
  public spawn(point: THREE.Vector3, dir: THREE.Vector3, headshot: boolean): void {
    this.buildBasis(dir)
    this.spawnSpray(point, headshot, headshot ? 1 : 0.6)
    this.spawnWallSpatter(point, headshot ? 30 : 20, headshot ? 1.15 : 0.85)
    this.spawnFloorSpatter(point, headshot ? 4 : 2, headshot ? 16 : 11)
  }

  /** A kill: a strong spray, wall spatter, and a growing pool of blood plus
   * heavy spatter on the floor under the body. */
  public spawnDeath(
    point: THREE.Vector3,
    dir: THREE.Vector3,
    headshot: boolean,
  ): void {
    this.buildBasis(dir)
    this.spawnSpray(point, headshot, headshot ? 1.6 : 1.25)
    this.spawnWallSpatter(point, headshot ? 40 : 30, headshot ? 1.25 : 1)
    this.spawnFloorPool(point, headshot)
  }

  private buildBasis(dir: THREE.Vector3): void {
    // Dedicated vectors (not the tmp scratch, which lookAt calls clobber).
    this.forward.copy(dir)
    if (this.forward.lengthSq() < 1e-6) this.forward.set(0, 0, 1)
    this.forward.normalize() // bullet travel
    this.backDir.copy(this.forward).multiplyScalar(-1)
    this.right.crossVectors(this.forward, UP)
    if (this.right.lengthSq() < 1e-5) this.right.set(1, 0, 0)
    this.right.normalize()
    this.up.crossVectors(this.right, this.forward).normalize()
  }

  /** Airborne spray — fast fading mist + heavier drops that fall and splat. */
  private spawnSpray(
    point: THREE.Vector3,
    headshot: boolean,
    intensity: number,
  ): void {
    const forward = this.forward // set by buildBasis
    const back = this.backDir
    const count = Math.round((headshot ? 46 : 30) * intensity)
    for (let i = 0; i < count; i += 1) {
      const mist = i < count * 0.4
      const base = i % 3 === 0 ? back : forward
      const velocity = base
        .clone()
        .multiplyScalar((mist ? 90 : 60) + Math.random() * (headshot ? 200 : 120))
        .addScaledVector(this.right, (Math.random() - 0.5) * (headshot ? 200 : 130))
        .addScaledVector(this.up, (Math.random() - 0.15) * (headshot ? 150 : 95))
      this.spawnParticle(point, back, velocity, mist, headshot)
    }
  }

  /** Cast the shot through the victim and splat blood onto the wall behind it
   * (only when a surface is actually there — no more decals floating in air). */
  private spawnWallSpatter(
    point: THREE.Vector3,
    size: number,
    opacity: number,
  ): void {
    if (!this.castRay) return
    const hit = this.castRay(point, this.forward, 180)
    if (!hit) return
    this.surfaceNormal(hit, this.forward)
    this.spawnSplatOn(hit.point, this.normal, size * (0.8 + Math.random() * 0.5), opacity)
    const satellites = 3
    for (let i = 0; i < satellites; i += 1) {
      this.tmp2
        .copy(hit.point)
        .addScaledVector(this.right, (Math.random() - 0.5) * size * 1.2)
        .addScaledVector(this.up, (Math.random() - 0.2) * size)
      this.spawnSplatOn(
        this.tmp2,
        this.normal,
        size * (0.3 + Math.random() * 0.5),
        opacity * (0.6 + Math.random() * 0.4),
      )
    }
  }

  private spawnFloorSpatter(
    point: THREE.Vector3,
    count: number,
    spread: number,
  ): void {
    if (!this.castRay) return
    this.floorOrigin.copy(point)
    this.floorOrigin.y += 8
    const hit = this.castRay(this.floorOrigin, this.down, 220)
    if (!hit) return
    this.surfaceNormal(hit, this.down)
    for (let i = 0; i < count; i += 1) {
      this.tmp2
        .copy(hit.point)
        .add(
          this.tmp.set(
            (Math.random() - 0.5) * spread * 2,
            0,
            (Math.random() - 0.5) * spread * 2,
          ),
        )
      this.spawnSplatOn(
        this.tmp2,
        this.normal,
        spread * (0.5 + Math.random() * 0.7),
        0.7 + Math.random() * 0.25,
      )
    }
  }

  /** The pool under a corpse: one big growing pool, a couple of spreading
   * satellite pools, and a ring of static spatter. */
  private spawnFloorPool(point: THREE.Vector3, headshot: boolean): void {
    if (!this.castRay) return
    this.floorOrigin.copy(point)
    this.floorOrigin.y += 8
    const hit = this.castRay(this.floorOrigin, this.down, 260)
    if (!hit) return
    this.surfaceNormal(hit, this.down)

    this.spawnPool(hit.point, this.normal, headshot ? 82 : 66, 2.0)
    const satellites = headshot ? 3 : 2
    for (let i = 0; i < satellites; i += 1) {
      this.tmp2
        .copy(hit.point)
        .add(
          this.tmp.set(
            (Math.random() - 0.5) * (headshot ? 70 : 50),
            0,
            (Math.random() - 0.5) * (headshot ? 70 : 50),
          ),
        )
      this.spawnPool(this.tmp2, this.normal, (headshot ? 40 : 30) * (0.6 + Math.random() * 0.6), 1.6)
    }
    this.spawnFloorSpatter(point, headshot ? 8 : 6, headshot ? 38 : 28)
  }

  /** Resolve a hit's world-space surface normal, flipped to face the shot. */
  private surfaceNormal(hit: THREE.Intersection, rayDir: THREE.Vector3): void {
    if (hit.face) {
      this.normal
        .copy(hit.face.normal)
        .transformDirection(hit.object.matrixWorld)
    } else {
      this.normal.copy(rayDir).negate()
    }
    if (this.normal.dot(rayDir) > 0) this.normal.negate()
    this.normal.normalize()
  }

  private spawnSplatOn(
    point: THREE.Vector3,
    normal: THREE.Vector3,
    size: number,
    opacity: number,
  ): void {
    const splat = this.splats[this.splatNext]
    this.splatNext = (this.splatNext + 1) % this.splats.length
    const material = splat.material as THREE.MeshBasicMaterial
    material.map = this.splatTextures[(Math.random() * this.splatTextures.length) | 0]
    material.opacity = opacity
    material.needsUpdate = true
    splat.position.copy(point).addScaledVector(normal, 0.7)
    splat.lookAt(this.tmp.copy(splat.position).add(normal))
    splat.rotation.z = Math.random() * Math.PI * 2
    splat.scale.set(size * (0.7 + Math.random() * 0.8), size, 1)
    splat.visible = true
  }

  private spawnPool(
    point: THREE.Vector3,
    normal: THREE.Vector3,
    maxSize: number,
    growTime: number,
  ): void {
    const pool = this.pools[this.poolNext]
    this.poolNext = (this.poolNext + 1) % this.pools.length
    pool.material.map = this.poolTextures[(Math.random() * this.poolTextures.length) | 0]
    pool.material.opacity = 0.92
    pool.material.needsUpdate = true
    pool.mesh.position.copy(point).addScaledVector(normal, 0.5)
    pool.mesh.lookAt(this.tmp.copy(pool.mesh.position).add(normal))
    pool.mesh.rotation.z = Math.random() * Math.PI * 2
    pool.jitter = 0.78 + Math.random() * 0.3
    pool.maxSize = maxSize
    pool.growTime = growTime
    pool.age = 0
    pool.active = true
    pool.mesh.visible = true
    this.scalePool(pool)
  }

  private scalePool(pool: BloodPool): void {
    const t = pool.growTime > 0 ? Math.min(1, pool.age / pool.growTime) : 1
    const grow = THREE.MathUtils.smoothstep(t, 0, 1)
    const s = pool.maxSize * (0.28 + 0.72 * grow)
    pool.mesh.scale.set(s * pool.jitter, s, 1)
  }

  private spawnParticle(
    point: THREE.Vector3,
    normal: THREE.Vector3,
    velocity: THREE.Vector3,
    mist: boolean,
    headshot: boolean,
  ): void {
    const particle = this.particles[this.particleNext]
    this.particleNext = (this.particleNext + 1) % this.particles.length
    particle.material.map = this.particleTextures[mist ? 0 : 2]
    particle.material.blending = mist
      ? THREE.AdditiveBlending
      : THREE.NormalBlending
    particle.material.opacity = mist ? 0.6 : 1
    particle.material.needsUpdate = true
    particle.mesh.position.copy(point).addScaledVector(normal, 2.5)
    particle.mesh.lookAt(this.tmp.copy(particle.mesh.position).add(normal))
    particle.mesh.rotation.z = Math.random() * Math.PI * 2
    const size = mist
      ? (headshot ? 5 : 3.5) + Math.random() * (headshot ? 9 : 6)
      : (headshot ? 2.2 : 1.4) + Math.random() * 3.4
    particle.mesh.scale.set(size * (0.65 + Math.random() * 0.85), size, 1)
    particle.velocity.copy(velocity)
    particle.maxLife = (mist ? MIST_LIFE : DROP_LIFE) * (0.75 + Math.random() * 0.55)
    particle.life = particle.maxLife
    particle.gravity = mist ? 45 : 320
    particle.drag = mist ? 2.6 : 0.65
    particle.spin = (Math.random() - 0.5) * (mist ? 10 : 18)
    particle.canSplat = !mist
    particle.mesh.visible = true
  }

  public update(dt: number): void {
    for (const particle of this.particles) {
      if (particle.life <= 0) continue
      particle.life -= dt
      if (particle.life <= 0) {
        particle.mesh.visible = false
        continue
      }
      const drag = Math.max(0, 1 - particle.drag * dt)
      particle.velocity.multiplyScalar(drag)
      particle.velocity.y -= particle.gravity * dt
      particle.mesh.position.addScaledVector(particle.velocity, dt)
      if (particle.canSplat && particle.velocity.y < 0 && this.castRay) {
        const hit = this.castRay(particle.mesh.position, this.down, 8)
        if (hit) {
          particle.canSplat = false
          this.surfaceNormal(hit, this.down)
          this.spawnSplatOn(
            hit.point,
            this.normal,
            5 + Math.random() * 8,
            0.65 + Math.random() * 0.22,
          )
        }
      }
      particle.mesh.rotation.z += particle.spin * dt
      particle.material.opacity = Math.min(1, particle.life / particle.maxLife)
    }

    for (const pool of this.pools) {
      if (!pool.active || pool.age >= pool.growTime) continue
      pool.age += dt
      this.scalePool(pool)
    }
  }

  public dispose(): void {
    for (const particle of this.particles) particle.mesh.removeFromParent()
    for (const splat of this.splats) splat.removeFromParent()
    for (const pool of this.pools) pool.mesh.removeFromParent()
  }
}
